import { useState, useEffect, useCallback } from 'react';
import { BookOpen, Star, Award, Clock, Eye } from 'lucide-react';
import MainLayout from '../../components/MainLayout/MainLayout';
import ContentCard from '../../components/Common/ContentCard';
import Carousel from '../../components/Common/Carousel';
import CarouselItem from '../../components/Common/CarouselItem';
import SecureImage from '../../components/Media/SecureImage';
import mediaService from '../../services/mediaService';
import type { UnifiedContentItem } from '../../types/unifiedContent';
import useSEO from '../../hooks/useSEO';
import { useMediaDuration, formatDuration } from '../../hooks/useMediaDuration';

const ContentsPage = () => {
  // Apply SEO for contents page
  useSEO();
  
  const [contents, setContents] = useState<UnifiedContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [stats, setStats] = useState({
    totalContents: 0,
    totalViews: 0,
    avgRating: 0,
  });




  // Fetch all contents data (no pagination needed for carousel)
  useEffect(() => {
    const fetchContents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch combined content and series for carousels
        const combinedData = await mediaService.getContentsCombined({
          page: 0,
          page_size: 100, // Get more items for carousels
        });
        
        setContents(combinedData || []);
        
        // Calculate basic stats from data
        setStats({
          totalContents: combinedData.length || 0,
          totalViews: combinedData.reduce((sum, item) => sum + (item.viewCount || 0), 0),
          avgRating: 0,
        });
      } catch (err) {
        console.error('Error fetching contents:', err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        if (errorMessage.includes('Authentication') || errorMessage.includes('credentials') || errorMessage.includes('session')) {
          setError('Unable to load content. Please log in to access this content.');
        } else if (errorMessage.includes('Network') || errorMessage.includes('connection')) {
          setError('Unable to load content. Please check your internet connection and refresh the page.');
        } else {
          setError(errorMessage || 'Unable to load content right now. Please refresh the page or try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContents();
  }, []);

  // Smart grouping with diverse content types and fallbacks
  const getGroupedContents = useCallback(() => {
    // Sort by newest first (default Netflix-style behavior)
    const sortedItems = [...contents].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    // If we have very limited content (less than 10 items), use a single mixed row
    if (sortedItems.length < 10) {
      return {
        mixed: sortedItems, // Single row with all content mixed together
      };
    }

    const filteredGroups: {[key: string]: UnifiedContentItem[]} = {};
    const usedIds = new Set<string>();
    const minItemsPerSection = 3; // Lower threshold for more sections

    // 1. Featured section - items that are explicitly featured
    const featured = sortedItems.filter(item => item.isFeatured).slice(0, 12);
    if (featured.length >= minItemsPerSection) {
      filteredGroups.featured = featured;
      featured.forEach(item => usedIds.add(item.id));
    }

    // 2. Trending section - high view count items not already used
    const trending = sortedItems
      .filter(item => !usedIds.has(item.id) && (item.viewCount || 0) > 0)
      .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
      .slice(0, 12);
    
    if (trending.length >= minItemsPerSection) {
      filteredGroups.trending = trending;
      trending.forEach(item => usedIds.add(item.id));
    }

    // 3. See More section - any remaining items
    const seeMoreItems = sortedItems.filter(item => !usedIds.has(item.id));
    if (seeMoreItems.length > 0) {
      filteredGroups.seeMore = seeMoreItems;
    }

    // If no sections were created (not enough items per type), create mixed sections
    if (Object.keys(filteredGroups).length === 0) {
      const halfPoint = Math.ceil(sortedItems.length / 2);
      filteredGroups.featured = sortedItems.slice(0, halfPoint);
      if (sortedItems.length > halfPoint) {
        filteredGroups.seeMore = sortedItems.slice(halfPoint);
      }
    }

    return filteredGroups;
  }, [contents]);

  // Helper function to convert UnifiedContentItem to ContentCard props
  const convertToContentItem = (item: UnifiedContentItem) => {
    // Generate sample tags based on content type and category
    const generateTags = () => {
      const tags = [];
      
      // Add category as a tag if available
      if (item.category) {
        tags.push(item.category);
      }
      
      // Add content type specific tags
      tags.push('Content');
      
      // Add series-specific tag if it's a series
      if (item.type === 'SERIES') {
        tags.push('Series');
      }
      
      // Add featured tag if it's featured
      if (item.isFeatured) {
        tags.push('Featured');
      }
      
      return tags.slice(0, 4); // Limit to 4 tags
    };

    // Determine the correct type based on whether this is a series or content
    const cardType = item.type === 'SERIES' ? 'series' as const : 'content' as const;

    return {
      id: item.id,
      title: item.title,
      thumbnail: item.thumbnailUrl || '',
      type: cardType,
      tags: generateTags(),
      category: item.category,
      // Add series-related props if it's a series
      ...(item.type === 'SERIES' && {
        totalSeasons: item.totalSeasons,
        totalEpisodes: item.totalEpisodes,
        seriesType: item.contentType as 'CINEMA_SERIES' | 'CONTENT_SERIES' | 'PODCAST_SERIES' | 'ANIMATION_SERIES',
      }),
    };
  };

  const featuredContent = contents.find(item => item.isFeatured) || contents[0];
  const heroDuration = useMediaDuration(featuredContent?.mediaUrl, featuredContent?.durationSeconds);

  // Format view count for display
  const formatViewCount = (views: number): string => {
    if (views >= 1000000) {
      const millions = views / 1000000;
      return millions % 1 === 0 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
    } else if (views >= 1000) {
      const thousands = views / 1000;
      return thousands % 1 === 0 ? `${thousands.toFixed(0)}K` : `${thousands.toFixed(1)}K`;
    }
    return views.toString();
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading content...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <BookOpen className="h-12 w-12 mx-auto mb-2" />
              <p className="text-lg font-semibold">Error Loading Content</p>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <style>{`
        .carousel-container::-webkit-scrollbar {
          display: none;
        }
        .carousel-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .carousel-container:hover::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-slate-950 via-neutral-900 to-slate-950 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <BookOpen className="h-8 w-8 text-orange-400 mr-3" />
                  <span className="text-orange-400 font-semibold">StrymHub Content Collection</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  <span className="font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                    All Content
                  </span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Explore our complete collection of educational content and tutorials. Discover both standalone 
                  content and content series - all designed to help you learn and grow.
                </p>
                
                {/* Stats */}
                <div className="flex flex-wrap gap-8 mb-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                      {stats.totalContents}+
                    </div>
                    <div className="text-sm text-gray-400">Content Items</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                      {stats.totalViews >= 1000 ? `${formatViewCount(stats.totalViews)}+` : `${stats.totalViews}+`}
                    </div>
                    <div className="text-sm text-gray-400">Total Views</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-cyan-400 bg-clip-text text-transparent">
                      2
                    </div>
                    <div className="text-sm text-gray-400">Content Types</div>
                  </div>
                </div>

                {/* Animation Elements */}
                <div className="flex items-center space-x-4 mb-8">
                  <BookOpen className="w-6 h-6 text-orange-400 animate-pulse" />
                  <div className="flex space-x-2">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="w-3 h-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.2}s` }}
                      />
                    ))}
                  </div>
                  <Star className="w-6 h-6 text-yellow-400 animate-pulse" />
                </div>
              </div>
              
              {/* Featured Content */}
              {featuredContent && (
                <div className="relative">
                  <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                    <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 relative">
                      <SecureImage 
                        src={featuredContent.thumbnailUrl?.trim() ?
                             featuredContent.thumbnailUrl :
                             `https://picsum.photos/400/600?random=${featuredContent.id || 'featured'}`
                        } 
                        alt={featuredContent.title}
                        className="w-full h-full object-cover"
                      />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>
                      
                      <div className="absolute top-4 left-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
                        <Award className="w-4 h-4 mr-1" />
                        Content
                      </div>
                      
                      <div className="absolute bottom-6 left-6 right-6 text-white">
                        {featuredContent.category && (
                          <div className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium mb-2 inline-block">
                            {featuredContent.category}
                          </div>
                        )}
                        <h3 className="text-2xl font-bold mb-2">{featuredContent.title}</h3>
                        <p className="text-gray-200 text-sm mb-4 line-clamp-2">{featuredContent.description}</p>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {heroDuration ? formatDuration(heroDuration) : featuredContent.type === 'SERIES' ? `${featuredContent.totalEpisodes || 0} episodes` : '0:00'}
                          </div>
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {formatViewCount(featuredContent.viewCount || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="py-8">
          <div className="text-center mb-8 px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Browse All Content</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover educational content, tutorials, and guides. Explore both standalone content 
              and content series to expand your knowledge and skills.
            </p>
          </div>

        {/* Content Section */}
        <div className="py-8">
          {/* Carousels by Content Type */}
          {Object.entries(getGroupedContents()).map(([type, items]) => {
            if (items.length === 0) return null;
            
            const typeLabels: {[key: string]: string} = {
              mixed: 'Latest Content',
              featured: 'Featured',
              trending: 'Trending',
              seeMore: 'See More',
            };

            return (
              <div key={type} className="mb-6">
                <Carousel title={typeLabels[type]} id={`content-${type}`}>
                  {items.map((item) => {
                    const contentItem = convertToContentItem(item);
                    return (
                      <CarouselItem key={item.id}>
                        <ContentCard {...contentItem} />
                      </CarouselItem>
                    );
                  })}
                </Carousel>
              </div>
            );
          })}

          {/* Empty State */}
          {contents.length === 0 && !loading && (
            <div className="max-w-7xl mx-auto text-center py-12">
              <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No content found</h3>
              <p className="text-gray-600">
                Check back later for educational content and tutorials.
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </MainLayout>
  );
};

export default ContentsPage;